import { Dialog } from 'primereact/dialog';
import { ScrollPanel } from 'primereact/scrollpanel';
import React, { useEffect, useMemo, useState } from 'react';
import BaseButton from '../form/BaseButton.jsx';
import IconCloseFull from '../images/IconCloseFull.jsx';
import circle from '../../resources/images/circle.png';
import { getInitials } from '../../utils/getInitials.js';
import { InputSwitch } from 'primereact/inputswitch';
import { useSendCalendarMeetingInvitesMutation } from '../../api/calendar.js';
import { useGetParticipantRoleTypesQuery } from '../../api/misc.js';

const MeetingParticipantsInvitation = ({ visible, setVisible, calendarContacts, setCalendarContacts, meetingId }) => {
    const [sendCalendarMeetingInvites, { isLoading }] = useSendCalendarMeetingInvitesMutation();
    const { data: participantRoleTypes } = useGetParticipantRoleTypesQuery({});
    const roleOptions = useMemo(() => {
        if (participantRoleTypes?.response?.participant_role_types) {
            return participantRoleTypes.response.participant_role_types;
        }

        return [];
    }, [participantRoleTypes]);

    const [participants, setParticipants] = useState([]);
    const [invited, setInvited] = useState([]);
    const [modifiedInvited, setModifiedInvited] = useState(new Map());

    useEffect(() => {
        if (visible) {
            const initialParticipants = calendarContacts.filter((item) => item.email !== null);
            setParticipants(initialParticipants);

            setInvited(initialParticipants.map(() => false));
        }
    }, [visible, calendarContacts]);

    const hideDialog = () => {
        setVisible(false);
    };

    const handleOnChange = (value, index) => {
        setInvited((prev) => prev.map((item, idx) => (idx === index ? value : item)));
        setModifiedInvited((prev) => {
            const newMap = new Map(prev);
            newMap.set(participants[index].id, value);
            return newMap;
        });
    };

    const getStatus = (participant) => {
        let element = '';
        let className = 'w-fit rounded-md px-3 py-1 font-extrabold text-[10px] text-theme1-white mb-1';
        switch (participant.meeting_invitation) {
            case 'accepted':
                element = <div className={`bg-theme1-success ${className}`}>Accepted</div>;
                break;
            case 'declined':
                element = <div className={`bg-theme1-error ${className}`}>Declined</div>;
                break;
            case 'proposed-change':
                element = <div className={`bg-theme1-warning ${className}`}>Proposed Change</div>;
                break;
            case 'update-needed':
                element = <div className={`bg-theme1-warmGreen ${className}`}>Update Needed</div>;
                break;
            default:
                element = <div className={`bg-theme1-lightBtns ${className}`}>Invited</div>;
        }

        return <div>{element}</div>;
    };

    const sendInvites = async () => {
        const updatedParticipants = participants.map((participant) => ({
            ...participant,
            invited: modifiedInvited.has(participant.id) ? modifiedInvited.get(participant.id) : participant.invited
        }));

        const selected = Array.from(modifiedInvited.entries())
            .filter(([_, invited]) => invited)
            .map(([id]) => id);

        const params = {
            participants: selected
        };

        await sendCalendarMeetingInvites({ id: meetingId, params });

        setCalendarContacts(updatedParticipants);

        setModifiedInvited(new Map());

        hideDialog();
    };

    if (!visible) return null;

    return (
        <Dialog
            className='rounded-4xl'
            visible={visible}
            style={{ width: '400px' }}
            breakpoints={{ '420px': '320px' }}
            onHide={hideDialog}
            content={() => (
                <div className='flex flex-col justify-center items-center py-8 pt-12 bg-white rounded-4xl'>
                    <button onClick={hideDialog} className='absolute top-6 right-6'>
                        <IconCloseFull className='fill-theme1-text5' />
                    </button>
                    <div className='text-theme1-title2 w-full text-[22px] mb-[30px] leading-tight border-b border-solid border-theme1-cardStroke relative px-6'>
                        <div className='absolute bottom-0 top-auto inset-0 bg-gradient-to-t from-theme1-gradientRed to-transparent h-8'></div>
                        <div className={'font-bold text-center mb-[32px]'}>Send an invitation to</div>
                        {participants.length === 0 ? (
                            <>
                                <div className='text-theme1-cardStroke font-light text-center'>
                                    No available participants!
                                </div>
                                <img alt='No contacts' src={circle} className='mx-auto my-8' />
                            </>
                        ) : (
                            <ScrollPanel
                                style={{
                                    maxHeight: '300px',
                                    width: '100%',
                                    overflowY: 'scroll',
                                    scrollbarWidth: 'thin'
                                }}
                                className='custom-scrollbar'
                            >
                                {participants.map((participant, index) => {
                                    const email = participant.email ?? 'No Email';
                                    return (
                                        <div
                                            key={participant.id || index}
                                            className='flex items-center gap-3 pr-[10px] py-3'
                                        >
                                            <div className='flex items-center justify-center w-12 h-12 rounded-full bg-theme1-pinkPrimary flex-shrink-0'>
                                                <div className='text-lg text-theme1-primary font-bold'>
                                                    {getInitials(
                                                        participant.contact?.first_name +
                                                            ' ' +
                                                            participant.contact?.last_name
                                                    )}
                                                </div>
                                            </div>

                                            <div className='overflow-hidden break-words'>
                                                {participant.invited && <div>{getStatus(participant)}</div>}
                                                <h3 className='text-theme1-primary text-sm font-bold m-0'>
                                                    {participant.contact?.first_name} {participant.contact?.last_name}
                                                </h3>
                                                <p className='text-xs font-bold text-theme1-title2 my-1'>
                                                    {roleOptions.find((role) => role.value === participant.role)?.label}
                                                </p>
                                                <p className='text-xs text-theme1-title2 my-1'>{email}</p>
                                            </div>

                                            <InputSwitch
                                                inputId={index}
                                                name='invited'
                                                className='ml-auto w-12'
                                                checked={invited[index] || false}
                                                onChange={() => handleOnChange(!invited[index], index)}
                                            />
                                        </div>
                                    );
                                })}
                            </ScrollPanel>
                        )}
                    </div>
                    <div className='flex justify-between gap-4 w-full px-6'>
                        <BaseButton
                            className='btn-primary-outline w-[47%] flex-shrink-0'
                            textValue='Cancel'
                            onClick={hideDialog}
                        />

                        <BaseButton
                            className='btn-primary text-white w-[47%] flex-shrink-0'
                            textValue={isLoading ? 'Sending...' : 'Send'}
                            onClick={sendInvites}
                            disabled={isLoading}
                        />
                    </div>
                </div>
            )}
        ></Dialog>
    );
};

MeetingParticipantsInvitation.displayName = 'MeetingParticipantsInvitation';

export default MeetingParticipantsInvitation;
